import { TranslateService } from '@ngx-translate/core';

import { ContextSite } from '@core/services/site-context/site-context.service';

import { MapElement } from '@features/components/visualization/mapbox/map-elements/map-element';
import { MapSource } from '@features/components/visualization/mapbox/map-elements/map-source';
import { MapboxTooltip } from '@features/components/visualization/mapbox/mapbox-tooltip';

const SITE_SOURCE = 'geo-area-context-sites';
const SITE_LAYER = 'geo-area-context-sites-layer';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MapMouseEvent = any;

/**
 * Whether a map is about geographic zones: it renders some, or one is being drawn. The
 * second term matters because a zone drawn from scratch has no area to render until its
 * first vertex lands, which is exactly when its surroundings are most worth seeing.
 */
export function mapShowsZones(areaCount: number, isEditingZone: boolean): boolean {
  return areaCount > 0 || isEditingZone;
}

/** Sites are context for zones, so a map showing none has no use for them. */
export function shouldOfferSiteBackdrop(
  areaCount: number,
  isEditingZone: boolean
): boolean {
  return mapShowsZones(areaCount, isEditingZone);
}

/**
 * Zones are context for the entities that must respect them, so they are offered the other
 * way round: on a map with something to place against them, and never on one whose subject
 * is zones already.
 */
export function shouldOfferAreaBackdrop(
  showsZones: boolean,
  hasPlacedEntities: boolean
): boolean {
  return !showsZones && hasPlacedEntities;
}

export function sitesToFeatureCollection(sites: ContextSite[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: sites.map((site) => ({
      type: 'Feature' as const,
      properties: { uid: site.uid, name: site.name },
      geometry: {
        type: 'Point' as const,
        coordinates: [site.coords.lng, site.coords.lat]
      }
    }))
  };
}

/**
 * The site dots drawn underneath geographic zones. Owned here rather than by
 * `MapAreaEditor` because the backdrop is view state: it is loaded on demand from the map
 * control and stays put across the start and end of a drawing session.
 */
export class MapSiteContext implements MapElement {
  private readonly source: MapSource;
  private readonly eventCleanups: (() => void)[] = [];

  constructor(
    private readonly map: mapboxgl.Map,
    private readonly tooltip: MapboxTooltip,
    private readonly translate: TranslateService
  ) {
    this.source = new MapSource(SITE_SOURCE, this.map);
  }

  init(): void {
    this.map.addSource(SITE_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] }
    });

    this.map.addLayer({
      id: SITE_LAYER,
      type: 'circle',
      source: SITE_SOURCE,
      paint: {
        'circle-radius': 4,
        'circle-color': '#3f3f46',
        'circle-stroke-width': 1,
        'circle-stroke-color': '#ffffff'
      }
    });
  }

  registerEvents(): void {
    const onMouseEnter = (event: MapMouseEvent) => {
      const feature = event.features?.[0];
      if (feature?.geometry?.type !== 'Point') {
        return;
      }
      this.map.getCanvas().style.cursor = 'pointer';
      this.tooltip.showTooltip(event.lngLat.lng, feature.geometry.coordinates, {
        name: feature.properties?.['name'],
        content: [
          {
            label: this.translate.instant('table.columns.uid'),
            level: 0,
            value: feature.properties?.['uid']
          }
        ]
      });
    };

    const onMouseLeave = () => {
      this.map.getCanvas().style.cursor = '';
    };

    this.map.on('mouseenter', SITE_LAYER, onMouseEnter);
    this.map.on('mouseleave', SITE_LAYER, onMouseLeave);
    this.eventCleanups.push(() => {
      this.map.off('mouseenter', SITE_LAYER, onMouseEnter);
      this.map.off('mouseleave', SITE_LAYER, onMouseLeave);
    });
  }

  destroy(): void {
    this.eventCleanups.forEach((cleanup) => cleanup());
    this.eventCleanups.length = 0;
    this.source.cleanup();
  }

  /** Called from a component effect whenever the loaded or shown state changes. */
  sync(sites: ContextSite[]): void {
    this.source.updateSourceOnReady(sitesToFeatureCollection(sites));
  }
}
